import { useState, useEffect, useMemo } from 'react'
import { Typography, Breadcrumb, Select, Card, Row, Col, Statistic, Space, Button } from 'antd'
import { RocketOutlined } from '@ant-design/icons'
import { EditableTable } from '@/components/EditableTable'
import type { ColumnConfig, ChangeSet } from '@/components/EditableTable/types'
import { useRecvizPeriods, useRecvizData, useRecvizSummary, useUpdateRecvizData, useCalculationDetail, useTogglePeriod, useGenerateRecvizMulti } from '@/hooks/useRecviz'
import { useFilials, useServices } from '@/hooks/useRefs'
import type { RecvizDepartmentUpdate, IndicatorType, GenerateRecvizMultiResponse } from '@/api/recviz'
import { DualFieldCell } from '@/components/recviz/DualFieldCell'
import { SurgicalCell, ProfileTypeDetailedCell } from '@/components/recviz/ProfileTypeCell'
import { TrafficLight } from '@/components/recviz/TrafficLight'
import { CalculationDetailModal, GenerateMultiPeriodModal } from '@/components/recviz'

const { Title } = Typography

export default function RecvizConfiguratorPage() {
  const [selectedPeriod, setSelectedPeriod] = useState<string>('')
  const [filters, setFilters] = useState<{ fil_code?: number; serv_code?: number }>({})

  // Состояние модалки детализации
  const [modalState, setModalState] = useState<{
    visible: boolean
    departmentId: string
    departmentName: string
    indicatorType: IndicatorType
    indicatorLabel: string
  }>({
    visible: false,
    departmentId: '',
    departmentName: '',
    indicatorType: 'ikz',
    indicatorLabel: '',
  })

  // Состояние модалки мультипериодной генерации
  const [generateModalVisible, setGenerateModalVisible] = useState(false)
  const [generateResult, setGenerateResult] = useState<GenerateRecvizMultiResponse | null>(null)

  // Год для расчёта (из выбранного периода MMYY)
  const calcYear = selectedPeriod ? 2000 + parseInt(selectedPeriod.slice(2, 4), 10) : new Date().getFullYear()

  // Загрузка данных
  const { data: periods, isLoading: loadingPeriods } = useRecvizPeriods()
  const { data: departments, isLoading: loadingData } = useRecvizData(selectedPeriod, filters)
  const { data: summary } = useRecvizSummary(selectedPeriod)
  const { data: filials } = useFilials()
  const { data: services } = useServices()

  // Mutation для сохранения
  const updateMutation = useUpdateRecvizData(selectedPeriod)

  // Hooks для детализации расчёта
  const { data: calcDetailData } = useCalculationDetail(
    modalState.departmentId,
    modalState.indicatorType,
    calcYear,
    modalState.visible,
  )
  const togglePeriodMutation = useTogglePeriod(
    modalState.departmentId,
    modalState.indicatorType,
    calcYear,
  )

  // Mutation для мультипериодной генерации
  const generateMultiMutation = useGenerateRecvizMulti()

  const handleGenerateMulti = async (periods: string[], exportToCh: boolean) => {
    const result = await generateMultiMutation.mutateAsync({
      periods,
      export_to_ch: exportToCh,
    })
    setGenerateResult(result)
  }

  const openCalcDetail = (departmentId: string, departmentName: string, indicatorType: IndicatorType, indicatorLabel: string) => {
    setModalState({
      visible: true,
      departmentId,
      departmentName,
      indicatorType,
      indicatorLabel,
    })
  }

  const closeCalcDetail = () => {
    setModalState(prev => ({ ...prev, visible: false }))
  }

  // Опции для select-полей
  const filialOptions = useMemo(() =>
    filials?.map(f => ({ value: f.fil_code, label: f.fil_name })) || [],
    [filials]
  )

  const serviceOptions = useMemo(() =>
    services?.map(s => ({ value: s.serv_code, label: s.serv_name })) || [],
    [services]
  )


  // Установить первый период по умолчанию
  useEffect(() => {
    if (periods && periods.length > 0 && !selectedPeriod) {
      setSelectedPeriod(periods[0].period)
    }
  }, [periods, selectedPeriod])

  // Конфигурация колонок таблицы
  const columns: ColumnConfig[] = useMemo(() => [
    {
      field: 'code_otd',
      title: 'Код отделения',
      type: 'text',
      width: 120,
      editable: false,
    },
    {
      field: 'code_otd_name_full',
      title: 'Название',
      type: 'text',
      width: 300,
      editable: false,
    },
    {
      field: 'fil_code',
      title: 'Филиал',
      type: 'select',
      width: 180,
      editable: true,
      options: filialOptions,
    },
    {
      field: 'serv_code',
      title: 'Служба',
      type: 'select',
      width: 180,
      editable: true,
      options: serviceOptions,
    },
    {
      field: 'is_surgical',
      title: 'Хир.',
      type: 'custom',
      width: 70,
      customRender: (_, record, onChange) => (
        <SurgicalCell
          isSurgical={record.is_surgical}
          autoIsSurgical={record.auto_is_surgical}
          onChange={(value) => onChange({ is_surgical: value })}
          editable={true}
        />
      ),
    },
    {
      field: 'profil_type_detailed',
      title: 'Тип профиля',
      type: 'custom',
      width: 120,
      customRender: (_, record, onChange) => (
        <ProfileTypeDetailedCell
          value={record.profil_type_detailed}
          autoValue={record.auto_profil_type_detailed}
          onChange={(value) => onChange({ profil_type_detailed: value })}
          editable={true}
        />
      ),
    },
    {
      field: 'status',
      title: 'Статус',
      type: 'text',
      width: 100,
      editable: false,
    },
    {
      field: 'kf_total_fact',
      title: 'Коек (факт)',
      type: 'number',
      width: 120,
      editable: false,
    },
    {
      field: 'kf_total_prikaz',
      title: 'Коек (приказ)',
      type: 'number',
      width: 120,
      editable: false,
    },
    {
      field: 'planned_cases',
      title: 'Плановые случаи',
      type: 'number',
      width: 140,
      editable: false,
    },
    // Вычисляемые показатели с dual-field паттерном
    {
      field: 'ikz',
      title: 'ИКЗ (плановый)',
      type: 'custom',
      width: 160,
      customRender: (_, record, onChange) => (
        <TrafficLight
          status={record.traffic_light_ikz || 'none'}
          tooltip={record.traffic_light_ikz !== 'none' ? 'Отклонение от планового значения' : undefined}
        >
          <DualFieldCell
            calculatedValue={record.calculated_ikz}
            manualValue={record.manual_ikz}
            onChange={(value) => onChange({ manual_ikz: value })}
            editable={true}
            suffix=""
            precision={3}
            onInfoClick={() => openCalcDetail(record.id_otd, record.code_otd_name_full, 'ikz', 'ИКЗ')}
            departmentId={record.id_otd}
            indicatorType="ikz"
            period={selectedPeriod}
          />
        </TrafficLight>
      ),
    },
    {
      field: 'sdpp',
      title: 'СДПП (плановая)',
      type: 'custom',
      width: 160,
      customRender: (_, record, onChange) => (
        <TrafficLight
          status={record.traffic_light_sdpp || 'none'}
          tooltip={record.traffic_light_sdpp !== 'none' ? 'Отклонение от планового значения' : undefined}
        >
          <DualFieldCell
            calculatedValue={record.calculated_sdpp}
            manualValue={record.manual_sdpp}
            onChange={(value) => onChange({ manual_sdpp: value })}
            editable={true}
            suffix="дн"
            precision={2}
            onInfoClick={() => openCalcDetail(record.id_otd, record.code_otd_name_full, 'sdpp', 'СДПП')}
            departmentId={record.id_otd}
            indicatorType="sdpp"
            period={selectedPeriod}
          />
        </TrafficLight>
      ),
    },
    {
      field: 'bed_work_days',
      title: 'Работа койки (дни)',
      type: 'custom',
      width: 180,
      customRender: (_, record, onChange) => (
        <TrafficLight
          status={record.traffic_light_bed_work || 'none'}
          tooltip={record.traffic_light_bed_work !== 'none' ? 'Отклонение от норматива' : undefined}
        >
          <DualFieldCell
            calculatedValue={record.normative_bed_work_days}
            manualValue={record.manual_bed_work_days}
            onChange={(value) => onChange({ manual_bed_work_days: value })}
            editable={true}
            suffix="дн"
            precision={0}
            normSource={record.norm_source}
            departmentId={record.id_otd}
            indicatorType="bed_work"
            period={selectedPeriod}
          />
        </TrafficLight>
      ),
    },
    // Доля плановых случаев (%)
    {
      field: 'pct_planned',
      title: '% плановых',
      type: 'custom',
      width: 130,
      customRender: (_, record, onChange) => (
        <DualFieldCell
          calculatedValue={record.calculated_pct_planned}
          manualValue={record.manual_pct_planned}
          onChange={(value) => onChange({ manual_pct_planned: value })}
          editable={true}
          suffix="%"
          precision={0}
          departmentId={record.id_otd}
          indicatorType="pct_planned"
          period={selectedPeriod}
        />
      ),
    },
  ], [filialOptions, serviceOptions, selectedPeriod])

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item>Оргструктура и реквизиты</Breadcrumb.Item>
        <Breadcrumb.Item>Конфигуратор</Breadcrumb.Item>
      </Breadcrumb>

      <Title level={2}>Конфигуратор recviz v2</Title>

      {/* Селектор периода и фильтры */}
      <Card style={{ marginBottom: 16 }}>
        <Space size="large" style={{ width: '100%', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', marginBottom: 8 }}>Период:</label>
            <Select
              style={{ width: 200 }}
              placeholder="Выберите период"
              value={selectedPeriod}
              onChange={setSelectedPeriod}
              loading={loadingPeriods}
              options={periods?.map(p => ({ value: p.period, label: p.label || p.period })) || []}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 8 }}>Филиал:</label>
            <Select
              style={{ width: 200 }}
              placeholder="Все"
              allowClear
              value={filters.fil_code}
              onChange={(value) => setFilters({ ...filters, fil_code: value })}
              options={filials?.map(f => ({ value: f.fil_code, label: f.fil_name })) || []}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 8 }}>Служба:</label>
            <Select
              style={{ width: 200 }}
              placeholder="Все"
              allowClear
              value={filters.serv_code}
              onChange={(value) => setFilters({ ...filters, serv_code: value })}
              options={services?.map(s => ({ value: s.serv_code, label: s.serv_name })) || []}
            />
          </div>

          {/* Кнопка формирования справочника реквизитов */}
          <div style={{ marginLeft: 'auto' }}>
            <label style={{ display: 'block', marginBottom: 8 }}>&nbsp;</label>
            <Button
              type="primary"
              icon={<RocketOutlined />}
              onClick={() => { setGenerateResult(null); setGenerateModalVisible(true) }}
            >
              Сформировать справочник
            </Button>
          </div>
        </Space>
      </Card>

      {/* Сводные карточки */}
      {summary && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Всего отделений"
                value={summary.total_departments}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Привязано"
                value={summary.departments_bound}
                suffix={`/ ${summary.total_departments}`}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Коек (факт)"
                value={summary.total_beds_fact || 0}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Коек (приказ)"
                value={summary.total_beds_prikaz || 0}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Модалка детализации расчёта */}
      <CalculationDetailModal
        visible={modalState.visible}
        onClose={closeCalcDetail}
        departmentName={modalState.departmentName}
        indicatorType={modalState.indicatorType}
        indicatorLabel={modalState.indicatorLabel}
        data={calcDetailData ?? null}
        onPeriodToggle={(period, included) => {
          togglePeriodMutation.mutate({ period, included })
        }}
        isToggling={togglePeriodMutation.isPending}
      />

      {/* Модалка мультипериодной генерации */}
      <GenerateMultiPeriodModal
        visible={generateModalVisible}
        onClose={() => setGenerateModalVisible(false)}
        availablePeriods={periods || []}
        onGenerate={handleGenerateMulti}
        loading={generateMultiMutation.isPending}
        result={generateResult}
      />

      {/* Таблица отделений */}
      <Card>
        <EditableTable
          columns={columns}
          dataSource={departments || []}
          rowKey="id_otd"
          loading={loadingData || updateMutation.isPending}
          onSave={async (changes: ChangeSet) => {
            // Преобразуем ChangeSet в формат API
            if (!changes.updated || Object.keys(changes.updated).length === 0) {
              return
            }

            const updates: Record<string, RecvizDepartmentUpdate> = {}

            for (const [id_otd, fields] of Object.entries(changes.updated)) {
              updates[id_otd] = {
                fil_code: fields.fil_code,
                serv_code: fields.serv_code,
                is_surgical: fields.is_surgical,
                profil_type_detailed: fields.profil_type_detailed,
                // Manual override для показателей
                manual_ikz: fields.manual_ikz,
                manual_sdpp: fields.manual_sdpp,
                manual_bed_work_days: fields.manual_bed_work_days,
              }
            }

            await updateMutation.mutateAsync(updates)
          }}
          draftKey={`recviz-${selectedPeriod}`}
          allowAdd={false}
          allowPaste={false}
          allowDelete={false}
        />
      </Card>
    </div>
  )
}
